import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import DataTable from "react-data-table-component";
import { GetData } from "../../services/api";
import { Link } from "react-router-dom";
import ReactPaginate from 'react-paginate';
import ManageContactListsModal from "../../components/ContactLists/ManageContactListsModal";
import "./leads.css";

const Leads = () => {
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [listsModal, setListsModal] = useState({ open: false, customerId: null, currentLists: [] });
  const itemsPerPage = 10;
  const searchTimeoutRef = useRef(null);

  // Información de paginación basada en el total del backend
  const pageCount = totalLeads > 0 ? Math.ceil(totalLeads / itemsPerPage) : 0;
  const paginationInfo = {
    start: totalLeads > 0 ? currentPage * itemsPerPage + 1 : 0,
    end: Math.min((currentPage + 1) * itemsPerPage, totalLeads),
    total: totalLeads,
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
    // Scroll suave hacia arriba
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Función para cargar leads desde el backend
  const fetchLeads = async (page = 0, search = "") => {
    try {
      // Solo mostrar loader en la carga inicial
      if (isInitialLoad) {
        setLoading(true);
      }
      
      const offset = page * itemsPerPage;
      const searchParam = search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "";
      const response = await GetData(`/customers?limit=${itemsPerPage}&offset=${offset}${searchParam}`);
      
      // GetData devuelve el objeto completo de axios, los datos están en response.data
      // El backend devuelve { data: [...], total: number, limit: number, offset: number }
      if (response && response.data) {
        const backendResponse = response.data;
        
        // Verificar si es la nueva estructura paginada
        if (backendResponse && backendResponse.data && typeof backendResponse.total === 'number') {
          const leadsData = backendResponse.data || [];
          const total = backendResponse.total || 0;
          setLeads(leadsData);
          setTotalLeads(total);
          console.log(`✅ Leads cargados: ${leadsData.length}, Total: ${total}, Páginas: ${Math.ceil(total / itemsPerPage)}`);
        } 
        // Fallback: si es un array directamente (estructura antigua)
        else if (Array.isArray(backendResponse)) {
          setLeads(backendResponse);
          setTotalLeads(backendResponse.length);
          console.log(`Leads cargados (array): ${backendResponse.length}`);
        } 
        // Si no hay datos
        else {
          console.log("No se detectó estructura válida:", backendResponse);
          setLeads([]);
          setTotalLeads(0);
        }
      } else {
        console.log("No hay response o response.data:", response);
        setLeads([]);
        setTotalLeads(0);
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
      if (error.response) {
        console.error("Error response:", error.response.data);
      }
      setLeads([]);
      setTotalLeads(0);
    } finally {
      setLoading(false);
      setIsInitialLoad(false); // Después de la primera carga, ya no es carga inicial
    }
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Nombre</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Apellidos</span>,
      selector: (row) => (row.lastName ? row.lastName : "-"),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Email</span>,
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Teléfono</span>,
      selector: (row) => <a href={`tel:${row.phone}`}>{row.phone}</a>,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Listas</span>,
      cell: (row) => {
        const contactLists = row.contactLists || [];
        if (contactLists.length === 0) {
          return <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: "11px" }}>Sin lista</span>;
        }
        return (
          <div className="d-flex flex-wrap gap-1">
            {contactLists.slice(0, 3).map((list, idx) => (
              <span
                key={list.id || idx}
                className="badge bg-primary-subtle text-primary"
                style={{ fontSize: "11px" }}
              >
                {list.name}
              </span>
            ))}
            {contactLists.length > 3 && (
              <span
                className="badge bg-info-subtle text-info"
                style={{ fontSize: "11px" }}
                title={`Y ${contactLists.length - 3} más`}
              >
                +{contactLists.length - 3}
              </span>
            )}
          </div>
        );
      },
      sortable: false,
      minWidth: "150px",
    },
    {
      name: <span className="font-weight-bold fs-13">Creado</span>,
      selector: (row) => moment(row.createdAt).format("DD/MM/YYYY HH:mm"),
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Acción</span>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <Link to={`/leads/${row.id}`}>
            <Button
              color="primary"
              size="sm"
              className="btn-soft-primary"
              style={{
                backgroundColor: '#E9553E',
                borderColor: '#E9553E',
                color: '#fff'
              }}
            >
              <i className="ri-eye-line me-1"></i>
              Detalle
            </Button>
          </Link>
          <Button
            color="primary"
            size="sm"
            outline
            title="Gestionar listas"
            onClick={() =>
              setListsModal({
                open: true,
                customerId: row.id,
                currentLists: row.contactLists || [],
              })
            }
          >
            <i className="ri-list-settings-line"></i>
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    }
  ];

  // Manejar búsqueda con debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Resetear a la primera página cuando se busca
    setCurrentPage(0);
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce: esperar 500ms después de que el usuario deje de escribir
    searchTimeoutRef.current = setTimeout(() => {
      fetchLeads(0, value);
    }, 500);
  };

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Cargar leads cuando cambia la página o el término de búsqueda
  useEffect(() => {
    fetchLeads(currentPage, searchTerm);
  }, [currentPage]);

  // Debug: Log de valores de paginación
  useEffect(() => {
    if (totalLeads > 0) {
      console.log(`📊 Paginación - Total: ${totalLeads}, Páginas: ${pageCount}, Página actual: ${currentPage + 1}, Mostrar paginador: ${pageCount > 1}`);
    }
  }, [totalLeads, pageCount, currentPage]);

  return (
    <div className="page-content" style={{ overflowY: 'auto', minHeight: 'calc(100vh - 70px)' }}>
      <Container fluid={true} style={{ paddingBottom: '3rem' }}>
        <Breadcrumbs title="IHubG" breadcrumbItem="Leads" />
        <Row className="mb-3">
          <Col className="col-sm-auto">
            <div className="d-flex gap-2 justify-content-end">
              <Link to="/leads/new">
                <Button
                  color="success"
                  className="add-btn"
                  style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
                >
                  <i className="ri-add-line align-bottom me-1"></i>
                  Nuevo Lead
                </Button>
              </Link>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <CardBody>
                {/* Barra de búsqueda */}
                <div className="mb-3">
                  <InputGroup>
                    <InputGroupText>
                      <i className="ri-search-line"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Buscar por nombre, email, teléfono, ciudad, dirección..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{ fontSize: "14px" }}
                    />
                    {searchTerm && (
                      <Button
                        color="link"
                        onClick={() => {
                          setSearchTerm("");
                          setCurrentPage(0);
                          fetchLeads(0, "");
                        }}
                        style={{ color: "#6c757d" }}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    )}
                  </InputGroup>
                </div>

                {loading ? (
                  <Loader size={150} />
                ) : (
                  <>
                    {/* Información de paginación */}
                    {totalLeads > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="text-muted" style={{ fontSize: "14px" }}>
                          Mostrando <strong>{paginationInfo.start}</strong> - <strong>{paginationInfo.end}</strong> de <strong>{paginationInfo.total}</strong> leads
                        </div>
                      </div>
                    )}

                    {/* Tabla de leads */}
                    <DataTable
                      data={leads}
                      columns={columns}
                      noDataComponent={
                        <div className="text-center py-5">
                          <i className="ri-inbox-line text-muted" style={{ fontSize: "48px" }}></i>
                          <p className="text-muted mt-3 mb-0">No hay leads disponibles</p>
                        </div>
                      }
                      customStyles={{
                        headRow: {
                          style: {
                            backgroundColor: "#f8f9fa",
                            borderBottom: "2px solid #dee2e6",
                          },
                        },
                        headCells: {
                          style: {
                            fontSize: "13px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          },
                        },
                        cells: {
                          style: {
                            fontSize: "14px",
                          },
                        },
                      }}
                    />

                    {/* Paginador moderno - Se muestra siempre que haya más de una página */}
                    {totalLeads > itemsPerPage ? (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                          Página {currentPage + 1} de {pageCount}
                        </div>
                        {pageCount > 1 && (
                          <ReactPaginate
                            breakLabel={<span className="px-2">...</span>}
                            nextLabel={
                              <span className="d-flex align-items-center">
                                Siguiente <i className="ri-arrow-right-s-line ms-1"></i>
                              </span>
                            }
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={5}
                            marginPagesDisplayed={2}
                            pageCount={pageCount}
                            previousLabel={
                              <span className="d-flex align-items-center">
                                <i className="ri-arrow-left-s-line me-1"></i> Anterior
                              </span>
                            }
                            renderOnZeroPageCount={null}
                            containerClassName="pagination pagination-rounded justify-content-end mb-0"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            activeClassName="active"
                            disabledClassName="disabled"
                            forcePage={currentPage}
                          />
                        )}
                      </div>
                    ) : totalLeads > 0 && totalLeads <= itemsPerPage ? (
                      <div className="text-center text-muted mt-3" style={{ fontSize: "13px" }}>
                        Mostrando todos los {totalLeads} leads
                      </div>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <ManageContactListsModal
          isOpen={listsModal.open}
          toggle={() => setListsModal((s) => ({ ...s, open: false }))}
          customerId={listsModal.customerId}
          currentLists={listsModal.currentLists}
          onDone={() => {}}
        />
      </Container>
    </div>
  );
};

export default Leads;



